import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Sun, Moon, Menu, X } from 'lucide-react';
import { toggleTheme } from '../lib/theme';
import { logout } from '../lib/auth';
import { useAuth, useTheme } from '../contexts';

export default function Navbar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAuthenticated: isAuthed, user } = useAuth();
  const { isDark } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const isAdmin = user?.is_staff || user?.is_superuser;
  const logoSrc = '/logo.svg';
  const titleSrc = '/header-title-decobank.svg';

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/login');
  };
  const closeMenu = () => {
    setMenuOpen(false);
  };
  return (
    <>
      <nav className="navbar">
        <div className="container-padded flex items-center justify-between py-4">
          <Link to="/" className="flex items-end gap-2.5">
            <img
              src={logoSrc}
              alt=""
              aria-hidden="true"
              className="h-9 w-9 rounded-lg object-cover shadow-sm dark:brightness-0 dark:invert"
            />
            <img
              src={titleSrc}
              alt="De CoBank"
              className="h-8 w-auto dark:brightness-0 dark:invert"
            />
          </Link>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Thema wisselen"
              className="btn-ghost h-10 w-10 !p-0"
              onClick={toggleTheme}
              title={isDark ? 'Naar lichte modus' : 'Naar donkere modus'}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              type="button"
              aria-label="Menu openen"
              className="btn-ghost h-10 w-10 !p-0"
              onClick={() => setMenuOpen(!menuOpen)}
              title="Menu"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </nav>

      {/* Backdrop overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-[45] transition-opacity"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      {/* Slide-in menu */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white dark:bg-secondary-900 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Menu header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-secondary-800">
            <span className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">Menu</span>
            <button
              type="button"
              aria-label="Menu sluiten"
              className="btn-ghost"
              onClick={closeMenu}
            >
              <X size={18} />
            </button>
          </div>

          {/* Menu content */}
          <div className="flex-1 overflow-y-auto p-4">
            <nav className="flex flex-col gap-2">
              {isAuthed ? (
                <>
                  <Link
                    to="/topics"
                    className={`block px-4 py-3 rounded-lg text-secondary-900 dark:text-secondary-100 hover:bg-primary-50 dark:hover:bg-secondary-800 transition-colors ${
                      pathname.startsWith('/topics') ? 'bg-primary-50 dark:bg-secondary-800 font-medium' : ''
                    }`}
                    onClick={closeMenu}
                  >
                    Onderwerpen
                  </Link>
                  <Link
                    to="/review"
                    className={`block px-4 py-3 rounded-lg text-secondary-900 dark:text-secondary-100 hover:bg-primary-50 dark:hover:bg-secondary-800 transition-colors ${
                      pathname.startsWith('/review') ? 'bg-primary-50 dark:bg-secondary-800 font-medium' : ''
                    }`}
                    onClick={closeMenu}
                  >
                    Vragenoverzicht
                  </Link>
                  <Link
                    to="/profile"
                    className={`block px-4 py-3 rounded-lg text-secondary-900 dark:text-secondary-100 hover:bg-primary-50 dark:hover:bg-secondary-800 transition-colors ${
                      pathname.startsWith('/profile') ? 'bg-primary-50 dark:bg-secondary-800 font-medium' : ''
                    }`}
                    onClick={closeMenu}
                  >
                    Profiel
                  </Link>
                  <Link
                    to="/support"
                    className={`block px-4 py-3 rounded-lg text-secondary-900 dark:text-secondary-100 hover:bg-primary-50 dark:hover:bg-secondary-800 transition-colors ${
                      pathname.startsWith('/support') ? 'bg-primary-50 dark:bg-secondary-800 font-medium' : ''
                    }`}
                    onClick={closeMenu}
                  >
                    Support
                  </Link>
                  {isAdmin && (
                    <>
                      <hr className="my-2 border-secondary-200 dark:border-secondary-700" />
                      <Link
                        to="/admin/dashboard"
                        className={`block px-4 py-3 rounded-lg text-secondary-900 dark:text-secondary-100 hover:bg-primary-50 dark:hover:bg-secondary-800 transition-colors ${
                          pathname.startsWith('/admin/dashboard') ? 'bg-primary-50 dark:bg-secondary-800 font-medium' : ''
                        }`}
                        onClick={closeMenu}
                      >
                        Dashboard
                      </Link>
                      <Link
                        to="/admin/analytics"
                        className={`block px-4 py-3 rounded-lg text-secondary-900 dark:text-secondary-100 hover:bg-primary-50 dark:hover:bg-secondary-800 transition-colors ${
                          pathname.startsWith('/admin/analytics') ? 'bg-primary-50 dark:bg-secondary-800 font-medium' : ''
                        }`}
                        onClick={closeMenu}
                      >
                        Analytics
                      </Link>
                      <Link
                        to="/admin/questions"
                        className={`block px-4 py-3 rounded-lg text-secondary-900 dark:text-secondary-100 hover:bg-primary-50 dark:hover:bg-secondary-800 transition-colors ${
                          pathname.startsWith('/admin/questions') ? 'bg-primary-50 dark:bg-secondary-800 font-medium' : ''
                        }`}
                        onClick={closeMenu}
                      >
                        Vragen bewerken
                      </Link>
                      <Link
                        to="/admin/import"
                        className={`block px-4 py-3 rounded-lg text-secondary-900 dark:text-secondary-100 hover:bg-primary-50 dark:hover:bg-secondary-800 transition-colors ${
                          pathname.startsWith('/admin/import') ? 'bg-primary-50 dark:bg-secondary-800 font-medium' : ''
                        }`}
                        onClick={closeMenu}
                      >
                        Vragen importeren
                      </Link>
                    </>
                  )}
                  <button
                    onClick={handleLogout}
                    className="btn-secondary w-full mt-4 justify-center"
                  >
                    Uitloggen
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`btn-secondary w-full mt-4 justify-center ${
                      pathname === '/login' ? 'ring-2 ring-primary-200' : ''
                    }`}
                    onClick={closeMenu}
                  >
                      Inloggen
                  </Link>
                  <Link
                    to="/register"
                    className="btn-primary w-full mt-4 justify-center"
                    onClick={closeMenu}
                  >
                    Registreren
                  </Link>
                  <Link
                    to="/support"
                    className={`block px-4 py-3 mt-6 rounded-lg text-secondary-900 dark:text-secondary-100 hover:bg-primary-50 dark:hover:bg-secondary-800 transition-colors ${
                      pathname.startsWith('/support') ? 'bg-primary-50 dark:bg-secondary-800 font-medium' : ''
                    }`}
                    onClick={closeMenu}
                  >
                    Support
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </div>
    </>
  );
}
